import { useCallback } from 'react';

export interface OfflineMeasurementListItem {
  id: number | null;
  uuid: string;
  typeOfMeasurementId: number | null;
  orderId: number | null;
  userId: number | null;
  createdAt: string;
  updatedAt: string;
  isCopy: number;
  isFinished: number;
  typeOfMeasurement: string;
  measurementLabel: string;
  roomName: string | null;
  pageCount: number;
  lastFinishedPage: number;
  orderUuid: string;
  isOfflineOnly: boolean;
}

interface OfflineMeasurementsState {
  [orderUuid: string]: OfflineMeasurementListItem[];
}

const STORAGE_KEY = 'offlineMeasurements';

// W tej samej karcie przeglądarki event `storage` nie odpala się.
// Dlatego emitujemy własny event, którego mogą słuchać hooki (np. lista pomiarów).
const emitChange = () => {
  try {
    window.dispatchEvent(
      new CustomEvent('offlineMeasurementsChanged', {
        detail: { ts: Date.now() },
      })
    );
  } catch {
    // no-op
  }
};

const readState = (): OfflineMeasurementsState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as OfflineMeasurementsState;
    return parsed || {};
  } catch (error) {
    console.error('Error reading offline measurements:', error);
    return {};
  }
};

const writeState = (state: OfflineMeasurementsState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error writing offline measurements:', error);
  }
};

export const addOfflineMeasurement = (
  measurement: OfflineMeasurementListItem
) => {
  const state = readState();
  const list = state[measurement.orderUuid] || [];
  const next: OfflineMeasurementsState = {
    ...state,
    [measurement.orderUuid]: [...list, measurement],
  };
  writeState(next);
  emitChange();
};

export const updateOfflineMeasurement = (
  orderUuid: string,
  uuid: string,
  patch: Partial<OfflineMeasurementListItem>
) => {
  const state = readState();
  const list = state[orderUuid] || [];
  const nextList = list.map((m) => (m.uuid === uuid ? { ...m, ...patch } : m));
  const next: OfflineMeasurementsState = {
    ...state,
    [orderUuid]: nextList,
  };
  writeState(next);
  emitChange();
};

export const removeOfflineMeasurement = (orderUuid: string, uuid: string) => {
  const state = readState();
  const list = state[orderUuid] || [];
  const nextList = list.filter((m) => m.uuid !== uuid);
  const next: OfflineMeasurementsState = {
    ...state,
    [orderUuid]: nextList,
  };
  writeState(next);
  emitChange();
};

export const getOfflineMeasurements = (
  orderUuid: string | null
): OfflineMeasurementListItem[] => {
  if (!orderUuid) return [];
  const state = readState();
  const list = state[orderUuid] || [];
  return list;
};

export const getOfflineMeasurementByUuid = (
  measurementUuid: string
): OfflineMeasurementListItem | null => {
  const state = readState();
  const all = Object.values(state).flat();
  return all.find((m) => m.uuid === measurementUuid) || null;
};

export const clearOfflineMeasurements = () => {
  writeState({});
  emitChange();
};

export const useOfflineMeasurements = () => {
  const getForOrder = useCallback(
    (orderUuid: string | null) => getOfflineMeasurements(orderUuid),
    []
  );

  return {
    getForOrder,
  };
};
